import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, errMsg, getAffiliate } from "@/lib/api";
import { hasAccess, useAuth } from "@/lib/auth";
import { Logo } from "@/components/AppShell";
import { Busy } from "@/components/ui-bits";

export default function AuthPage({ mode }: { mode: "login" | "register" }) {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(params.get("error") === "google" ? "Google sign-in didn't complete. Try again." : "");
  const resetDone = params.get("reset") === "1";
  const isLogin = mode === "login";

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.post(isLogin ? "/auth/login" : "/auth/register", isLogin ? { email: form.email, password: form.password } : { ...form, affiliate_ref: getAffiliate() });
      if (data.token) localStorage.setItem("bestie_token", data.token);
      setUser(data);
      nav(hasAccess(data) ? (data.onboarding_complete ? "/app" : "/onboarding") : data.stripe_subscription_id ? "/billing" : "/subscribe");
    } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <main className="min-h-screen bg-cream grid lg:grid-cols-2">
      <section className="hidden lg:flex flex-col justify-between bg-navy text-cream p-12 relative grain">
        <Logo light />
        <div className="relative">
          <p className="eyebrow !text-olive-light">Your AI email marketing manager</p>
          <h2 className="font-serif text-5xl mt-4 leading-tight">Know what to send.<br />Know when.<br /><span className="italic text-olive-light">Know what to say.</span></h2>
          <p className="text-cream/70 mt-6 max-w-md">Every recommendation explained. Every email in your voice. You always have the final say.</p>
        </div>
        <p className="text-xs text-cream/40">Founding members: $17/mo, locked in while active.</p>
      </section>
      <section className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md animate-rise">
          <div className="lg:hidden mb-8"><Logo /></div>
          <p className="eyebrow">{isLogin ? "Welcome back" : "Step 1 of 2"}</p>
          <h1 className="font-serif text-3xl sm:text-4xl mt-2">{isLogin ? "Log in" : "Create your account"}</h1>
          <p className="text-ink-muted text-sm mt-2">{isLogin ? "Pick up where you left off." : "Then you'll set up your $17/mo founding membership."}</p>

          <button onClick={google} className="btn-ghost w-full mt-8 !py-3" data-testid="google-auth-button">
            <svg className="size-4" viewBox="0 0 24 24"><path fill="#EA4335" d="M12 10.2v3.9h5.5c-.2 1.4-1.7 4.1-5.5 4.1-3.3 0-6-2.7-6-6.1s2.7-6.1 6-6.1c1.9 0 3.1.8 3.9 1.5l2.6-2.6C16.9 3.3 14.7 2.3 12 2.3 6.6 2.3 2.3 6.6 2.3 12s4.3 9.7 9.7 9.7c5.6 0 9.3-3.9 9.3-9.5 0-.6-.1-1.1-.2-1.6H12z"/></svg>
            Continue with Google
          </button>
          <div className="flex items-center gap-3 my-6 text-xs text-ink-soft"><span className="h-px flex-1 bg-line" />or with email<span className="h-px flex-1 bg-line" /></div>

          <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
            {isLogin && resetDone && <p className="text-sm text-olive-deep bg-olive-light rounded-xl px-4 py-3" data-testid="auth-reset-confirmation">Password changed. Log in with your new password.</p>}
            {!isLogin && <div><label className="label">Your name</label><input className="field" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="First name is fine" data-testid="auth-name-input" /></div>}
            <div><label className="label">Email</label><input type="email" className="field" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="auth-email-input" /></div>
            <div><div className="flex items-center justify-between"><label className="label">Password</label>{isLogin && <Link to="/forgot-password" className="text-xs text-olive-deep underline mb-1.5" data-testid="auth-forgot-link">Forgot password?</Link>}</div><input type="password" className="field" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={isLogin ? "" : "At least 8 characters"} data-testid="auth-password-input" /></div>
            {error && <p className="text-sm text-[#687000] bg-clay-light rounded-xl px-4 py-3" data-testid="auth-error">{error}</p>}
            <button className="btn-ink w-full !py-3.5" disabled={busy} data-testid="auth-submit-button">{busy ? <Busy label="One moment..." /> : isLogin ? "Log in" : "Create account"}</button>
          </form>
          <p className="text-sm text-ink-muted mt-6 text-center">
            {isLogin ? <>New here? <Link to="/register" className="font-semibold text-olive-deep underline" data-testid="auth-switch-register">Create an account</Link></> : <>Already a member? <Link to="/login" className="font-semibold text-olive-deep underline" data-testid="auth-switch-login">Log in</Link></>}
          </p>
        </div>
      </section>
    </main>
  );
}
